import { Injectable, inject } from "@angular/core";
import {
  FourCJourneyState,
  FourCJourneyStateInput,
  FourCJourneyStateResult,
} from "../interfaces/sales-journey-state.i";
import { LoggingService } from "./logging.service";

@Injectable({
  providedIn: "root",
})
export class SalesJourneyStateService {
  private readonly loggingService = inject(LoggingService);
  private lastLoggedState: FourCJourneyState | null = null;

  /**
   * Resolves explicit 4C journey state from identity, reconciliation, and payment inputs.
   * Repeat (DFCI) users never enter anonymous-only logic.
   */
  resolve(input: FourCJourneyStateInput = {}): FourCJourneyStateResult {
    const result = this.resolveInternal(input);
    this.logStateTransition(result);
    return result;
  }

  resetLoggedState(): void {
    this.lastLoggedState = null;
  }

  private resolveInternal(input: FourCJourneyStateInput): FourCJourneyStateResult {
    const isRepeatUserJourney = this.isRepeatUserJourney(input);
    const isAnonymousJourney = !isRepeatUserJourney && this.isAnonymousJourney(input);

    if (this.isReconciliationFailed(input)) {
      return {
        state: "identity_reconciliation_failed",
        canRender: true,
        canSubmitPayment: false,
        blockedReason: "Identity reconciliation failed. Please retry registration or contact support.",
        isAnonymousJourney,
        isRepeatUserJourney,
      };
    }

    if (this.isReconciliationPending(input)) {
      const canSubmitPayment = this.isReconciliationSafe(input) && !this.isPaymentBlocked(input);
      return {
        state: "identity_reconciliation_pending",
        canRender: true,
        canSubmitPayment,
        blockedReason: canSubmitPayment
          ? null
          : "Identity reconciliation is in progress. Payment will be available shortly.",
        isAnonymousJourney,
        isRepeatUserJourney,
      };
    }

    if (this.isPaymentBlocked(input)) {
      return {
        state: "payment_blocked",
        canRender: true,
        canSubmitPayment: false,
        blockedReason: this.resolvePaymentBlockedReason(input),
        isAnonymousJourney,
        isRepeatUserJourney,
      };
    }

    if (this.isPaymentReady(input)) {
      return {
        state: "payment_ready",
        canRender: true,
        canSubmitPayment: true,
        blockedReason: null,
        isAnonymousJourney,
        isRepeatUserJourney,
      };
    }

    if (isRepeatUserJourney) {
      return {
        state: "repeat_user_ready",
        canRender: true,
        canSubmitPayment: true,
        blockedReason: null,
        isAnonymousJourney: false,
        isRepeatUserJourney: true,
      };
    }

    return {
      state: "anonymous_ready",
      canRender: true,
      canSubmitPayment: true,
      blockedReason: null,
      isAnonymousJourney: true,
      isRepeatUserJourney: false,
    };
  }

  private isRepeatUserJourney(input: FourCJourneyStateInput): boolean {
    if (input.isAnonymous) {
      return false;
    }
    const userId = input.userId?.trim();
    if (!userId) {
      return false;
    }
    return userId.startsWith("DFCI") || (!input.anonymousId && !input.isAnonymous);
  }

  private isAnonymousJourney(input: FourCJourneyStateInput): boolean {
    if (input.isAnonymous) {
      return true;
    }
    return !!(input.anonymousId && !this.isRepeatUserJourney(input));
  }

  private isReconciliationFailed(input: FourCJourneyStateInput): boolean {
    const status = this.normalizeReconciliationStatus(input.reconciliationStatus);
    if (status === "failed") {
      return true;
    }
    // Signup status is anonymous-registration context only; repeat users may share
    // stale FAIL values on auth subjects from a prior anonymous attempt.
    if (!this.isAnonymousJourney(input)) {
      return false;
    }
    return this.normalizeSignupStatus(input.signupStatus) === "FAIL";
  }

  private isReconciliationPending(input: FourCJourneyStateInput): boolean {
    const status = this.normalizeReconciliationStatus(input.reconciliationStatus);
    if (status === "pending") {
      return true;
    }
    return (
      this.isAnonymousJourney(input) &&
      !input.customerId &&
      !this.isReconciliationFailed(input) &&
      this.normalizeSignupStatus(input.signupStatus) !== "SUCCESS"
    );
  }

  /** Anonymous purchase is allowed when backend marks reconciliation as safe or registration customer_id exists. */
  private isReconciliationSafe(input: FourCJourneyStateInput): boolean {
    const status = this.normalizeReconciliationStatus(input.reconciliationStatus);
    if (status === "safe" || status === "complete" || status === "not_required") {
      return true;
    }

    const eligibility = this.normalizePaymentEligibility(input.paymentEligibility);
    if (eligibility === "allowed") {
      return true;
    }
    if (eligibility === "blocked") {
      return false;
    }

    if (status === "pending") {
      return !!(input.isAnonymous && input.customerId);
    }

    return !this.isReconciliationPending(input);
  }

  private isPaymentBlocked(input: FourCJourneyStateInput): boolean {
    const eligibility = this.normalizePaymentEligibility(input.paymentEligibility);
    if (eligibility === "blocked") {
      return true;
    }
    return (
      input.hasPaymentCappingSyndicate === true &&
      input.purchaseCappingGoodForPayment === false
    );
  }

  private isPaymentReady(input: FourCJourneyStateInput): boolean {
    const eligibility = this.normalizePaymentEligibility(input.paymentEligibility);
    if (eligibility !== "allowed") {
      return false;
    }
    if (
      input.hasPaymentCappingSyndicate === true &&
      input.purchaseCappingGoodForPayment === false
    ) {
      return false;
    }
    return true;
  }

  private resolvePaymentBlockedReason(input: FourCJourneyStateInput): string {
    if (
      input.hasPaymentCappingSyndicate &&
      input.purchaseCappingGoodForPayment === false
    ) {
      return "Purchase capping applies to this offer.";
    }
    return "Payment is not available for this journey state.";
  }

  private normalizeReconciliationStatus(
    value: FourCJourneyStateInput["reconciliationStatus"]
  ): "pending" | "failed" | "complete" | "safe" | "not_required" | null {
    if (value == null || value === "") {
      return null;
    }
    const normalized = String(value).trim().toLowerCase();
    if (normalized === "fail" || normalized === "failure" || normalized === "error") {
      return "failed";
    }
    if (
      normalized === "pending" ||
      normalized === "in_progress" ||
      normalized === "in-progress" ||
      normalized === "processing"
    ) {
      return "pending";
    }
    if (
      normalized === "complete" ||
      normalized === "completed" ||
      normalized === "success" ||
      normalized === "done"
    ) {
      return "complete";
    }
    if (normalized === "safe") {
      return "safe";
    }
    if (normalized === "not_required" || normalized === "not-required" || normalized === "none") {
      return "not_required";
    }
    return null;
  }

  private normalizeSignupStatus(value: string | null | undefined): string | null {
    if (value == null || value === "") {
      return null;
    }
    return String(value).trim().toUpperCase();
  }

  private normalizePaymentEligibility(
    value: FourCJourneyStateInput["paymentEligibility"]
  ): "allowed" | "blocked" | null {
    if (value === true || value === "allowed" || value === "ALLOWED") {
      return "allowed";
    }
    if (value === false || value === "blocked" || value === "BLOCKED") {
      return "blocked";
    }
    return null;
  }

  private logStateTransition(result: FourCJourneyStateResult): void {
    if (this.lastLoggedState === result.state) {
      return;
    }
    const suffix = result.blockedReason ? ` — ${result.blockedReason}` : "";
    this.loggingService.logInfo(
      `[SalesJourneyState] ${this.lastLoggedState ?? "initial"} → ${result.state}${suffix}`
    );
    this.lastLoggedState = result.state;
  }
}
